/*
__/\\\\\\\\\\\\\\\__/\\\\\\\\\\\\\\\_____/\\\\\\\\\____        
 _\///////\\\/////__\///////\\\/////____/\\\\\\\\\\\\\__       
  _______\/\\\_____________\/\\\________/\\\/////////\\\_      
   _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_     
    _______\/\\\_____________\/\\\_______\/\\\\\\\\\\\\\\\_    
     _______\/\\\_____________\/\\\_______\/\\\/////////\\\_   
      _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_  
       _______\/\\\_____________\/\\\_______\/\\\_______\/\\\_ 
        _______\///______________\///________\///________\///__
            
            COPYRIGHT TACTICAL TRANSPORTATION ADVISORS, INC. 
            ALL RIGHTS RESERVED.
*/

import { useState } from "react";
import { Modal } from "react-bootstrap";
import moment from "moment";
import { OnboardingDocumentsReadOnly } from "../../../tools";
import CustomButton from "../../../components/CustomButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBriefcase, faCloudArrowDown, faIdCard, faTimes } from "@fortawesome/free-solid-svg-icons";
import PaychexInProgressWorkerModal from "./PaychexInProgressWorkerModal";
import EVerifyModal from "./EVerifyModal";

export default function DocumentRequestElement({ documentRequest, handleShowModal, handleDownloadDocumentRequest, handleDismissRequest, isHistorical = false, showStatus = false, onEVerifySuccess }) {
  const [isDismissing, setIsDismissing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showPaychexWorkerModal, setShowPaychexWorkerModal] = useState(false);
  const [showEVerifyModal, setShowEVerifyModal] = useState(false);

  const showEVerifyButton = documentRequest.hasCompletedI9 && !documentRequest.employee?.eVerify;

  const documentsString = Object.keys(documentRequest.documents)
    .filter((key) => OnboardingDocumentsReadOnly[key] !== undefined)
    .map((key) => OnboardingDocumentsReadOnly[key])
    .join(", ");

  return (
    <>
      <tr className="cursor-pointer" onClick={() => handleShowModal(documentRequest)}>
        <td>{documentRequest.employee.companyName}</td>
        <td>{documentRequest.employee.csaName}</td>
        <td>{documentRequest.employee.state}</td>
        <td>
          {documentRequest.employee?.firstName} {documentRequest.employee?.lastName}
        </td>
        <td>{moment(documentRequest.dateCreated).format("MMM D, YYYY")}</td>
        {isHistorical && (
          <td>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span>{moment(documentRequest.adminDateDismissed).format("MMM D, YYYY")}</span>
              <span style={{ color: "#6c757d", fontSize: "0.85em" }}>{moment(documentRequest.adminDateDismissed).format("h:mm A")}</span>
            </div>
          </td>
        )}
        <td>{documentsString}</td>
        <td>{documentRequest.paychexWorkerId ?? ''}</td>
        {showStatus && (
          <td>
            {documentRequest.employerCompleted ? (
              <span style={{ color: "#198754", fontWeight: 500, whiteSpace: "nowrap" }}>Employer Completed</span>
            ) : (
              <span style={{ color: "#fd7e14", fontWeight: 500, whiteSpace: "nowrap" }}>Awaiting Employer</span>
            )}
          </td>
        )}
        <td>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 4, width: "100%" }}>
            <CustomButton
              label={<FontAwesomeIcon icon={faCloudArrowDown} />}
              isLoading={isDownloading}
              onClick={(event) => {
                event.stopPropagation();
                handleDownloadDocumentRequest(documentRequest, setIsDownloading);
              }}
            />
            {showEVerifyButton && (
              <CustomButton
                label={<FontAwesomeIcon icon={faIdCard} />}
                title="Assign E-Verify"
                onClick={(event) => {
                  event.stopPropagation();
                  setShowEVerifyModal(true);
                }}
              />
            )}
            {!isHistorical && (
              <CustomButton
                label={<FontAwesomeIcon icon={faBriefcase} />}
                disabled={documentRequest.migrated}
                title={documentRequest.migrated ? "This document request has been migrated to a new system and cannot be modified." : "Submit to Paychex"}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowPaychexWorkerModal(true);
                }}
              />
            )}
            {!isHistorical && (
              <CustomButton
                label={<FontAwesomeIcon icon={faTimes} />}
                style={{ backgroundColor: documentRequest.migrated ? "#aaa" : "firebrick" }}
                disabled={documentRequest.migrated}
                title={documentRequest.migrated ? "This document request has been migrated to a new system and cannot be modified." : "Dismiss"}
                isLoading={isDismissing}
                onClick={(event) => {
                  event.stopPropagation();
                  setShowConfirmation(true);
                }}
              />
            )}
          </div>
        </td>
      </tr>
      <Modal
        show={showConfirmation}
        onHide={() => {
          setShowConfirmation(false);
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Dismiss Document Request?</Modal.Title>
        </Modal.Header>
        <Modal.Body>The document request will no longer be visible here, but will still be accessible on the Tactical Contractor's Human Resources page.</Modal.Body>
        <Modal.Footer>
          <CustomButton isLoading={isDismissing} label="Dismiss Document Request" onClick={() => handleDismissRequest(documentRequest, setIsDismissing, setShowConfirmation)} />
        </Modal.Footer>
      </Modal>
      <Modal
        show={showPaychexWorkerModal}
        onHide={() => {
          setShowPaychexWorkerModal(false);
        }}
        fullscreen
        centered
      >
        <PaychexInProgressWorkerModal documentRequestId={documentRequest.uid} hideModal={() => setShowPaychexWorkerModal(false)} />
      </Modal>
      <Modal show={showEVerifyModal} onHide={() => setShowEVerifyModal(false)} centered>
        <EVerifyModal documentRequest={documentRequest} hideModal={() => setShowEVerifyModal(false)} onSuccess={onEVerifySuccess} />
      </Modal>
    </>
  );
}
